export enum TaskType {
  CONNECTION_UNAUTHORIZED = 'connection_unauthorized',
  FORECAST_CHANGED = 'forecast_changed',
  PRODUCTS_ADDED = 'products_added',
  REPLENISH = 'replenish',
  SHIPMENT_OVERDUE = 'shipment_overdue',
  SHIPMENT_INCOMING = 'shipment_incoming',
}

export interface TaskWeeklyPoint {
  year: number;
  week: number;
  quantity: number;
}

export interface TaskProductBase {
  productId: string;
  title?: string;
  image?: string;
}

export interface ForecastChangedProduct extends TaskProductBase {
  quantity?: number;
  averageWeeklySales?: number;
  changePercent?: number;
  orderShiftDays?: number;
  orderWithinDays?: number;
  weeksOfStockLeft?: number;
  weeksForecasted?: number;
  previousWeekly?: TaskWeeklyPoint[];
  currentWeekly?: TaskWeeklyPoint[];
}

export interface ForecastChangedMetadata {
  product: ForecastChangedProduct;
}

export type ReplenishStockStatus = 'low_stock' | 'out_of_stock';

export interface ReplenishProduct extends TaskProductBase {
  quantity?: number;
  weeksOfStockLeft?: number;
  orderWithinDays?: number;
  weeksForecasted?: number;
  stockStatus?: ReplenishStockStatus;
}

export interface ReplenishMetadata {
  product: ReplenishProduct;
}

export interface ProductsAddedMetadata {
  product: TaskProductBase;
}

export interface ShipmentProduct extends TaskProductBase {
  quantity?: number;
}

export interface ShipmentOverdueMetadata {
  product: ShipmentProduct;
}

export interface ShipmentIncomingMetadata {
  product: ShipmentProduct;
  etaDate: string;
  totalQty: number;
}

export interface ConnectionUnauthorizedMetadata {
  connectionId: string;
  connectionName?: string;
}

export interface TaskMetadataByType {
  [TaskType.CONNECTION_UNAUTHORIZED]: ConnectionUnauthorizedMetadata;
  [TaskType.FORECAST_CHANGED]: ForecastChangedMetadata;
  [TaskType.REPLENISH]: ReplenishMetadata;
  [TaskType.PRODUCTS_ADDED]: ProductsAddedMetadata;
  [TaskType.SHIPMENT_OVERDUE]: ShipmentOverdueMetadata;
  [TaskType.SHIPMENT_INCOMING]: ShipmentIncomingMetadata;
}

export type TaskMetadata = TaskMetadataByType[TaskType];

/** Filled when grouping per-product tasks for display. */
export interface GroupedTaskMetadata {
  product?: TaskProductBase;
  products: TaskProductBase[];
  totalQty?: number;
  etaDate?: string;
}

export const isGroupedTaskMetadata = (
  metadata?: TaskMetadata | GroupedTaskMetadata,
): metadata is GroupedTaskMetadata =>
  Array.isArray((metadata as GroupedTaskMetadata | undefined)?.products);

export const isConnectionUnauthorizedMetadata = (
  metadata?: TaskMetadata | GroupedTaskMetadata,
): metadata is ConnectionUnauthorizedMetadata =>
  typeof (metadata as ConnectionUnauthorizedMetadata | undefined)?.connectionId === 'string';

export interface AppTask {
  id: string;
  companyId: string;
  type: TaskType;
  title: string;
  subtitle?: string;
  metadata: TaskMetadata | GroupedTaskMetadata;
  finished: boolean;
  finishedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  /** Set when multiple per-product tasks are grouped for display. */
  sourceIds: string[];
}
